import React, { useState } from 'react';
import { Search, MapPin, ExternalLink, Star } from 'lucide-react';
import { restaurantsApi } from '../api/services';
import type { RestaurantResult } from '../types/models';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { LinearProgressBar } from '@/components/ui/linear-progress';
import { cn } from '@/lib/utils';

interface RestaurantSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (restaurant: RestaurantResult) => void;
}

const PRICE_LABELS: Record<number, string> = {
  1: '£',
  2: '££',
  3: '£££',
  4: '££££',
};

// Simple star rating display. Rounds to the nearest half-star using a clipped
// fill overlay so we don't need a custom SVG.
function StarRating({ value }: { value: number }) {
  const rounded = Math.round(value * 2) / 2; // half-step
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const fillPercent = rounded >= i ? 100 : rounded >= i - 0.5 ? 50 : 0;
        return (
          <span key={i} className="relative inline-block h-3.5 w-3.5">
            <Star className="absolute inset-0 h-3.5 w-3.5 text-amber-300" />
            {fillPercent > 0 && (
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 overflow-hidden"
                style={{ width: `${fillPercent}%` }}
              >
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

export default function RestaurantSearchDialog({ open, onClose, onSelect }: RestaurantSearchDialogProps) {
  const [location, setLocation] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [results, setResults] = useState<RestaurantResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!location.trim()) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const data = await restaurantsApi.search(location.trim(), cuisine.trim() || undefined);
      setResults(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to search restaurants');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (restaurant: RestaurantResult) => {
    onSelect(restaurant);
    onClose();
    setResults([]);
    setSearched(false);
    setLocation('');
    setCuisine('');
  };

  const handleClose = () => {
    onClose();
    setResults([]);
    setSearched(false);
    setError(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Book Restaurant</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="rsd-loc">Location</Label>
              <Input
                id="rsd-loc"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Canary Wharf, Mayfair"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:w-56">
              <Label htmlFor="rsd-cuisine">Cuisine (optional)</Label>
              <Input
                id="rsd-cuisine"
                value={cuisine}
                onChange={(e) => setCuisine(e.target.value)}
                placeholder="e.g. Italian, Japanese"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={loading || !location.trim()}
              className="gap-1.5 self-end whitespace-nowrap sm:min-w-[120px]"
            >
              <Search className="h-4 w-4" />
              Search
            </Button>
          </div>

          {loading && <LinearProgressBar />}

          {error && (
            <Alert variant="error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {searched && !loading && results.length === 0 && !error && (
            <div className="py-6 text-center">
              <MapPin className="mx-auto mb-1 h-12 w-12 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No restaurants found for this search</p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {results.map((restaurant) => (
              <Card key={restaurant.placeId} className="flex h-full flex-col transition-colors hover:border-primary">
                {restaurant.photoUrl && (
                  <img
                    src={restaurant.photoUrl}
                    alt={restaurant.name}
                    className="h-[120px] w-full rounded-t-lg object-cover"
                  />
                )}
                <CardContent className="flex-1 py-3">
                  <p className="mb-1 text-sm font-semibold">{restaurant.name}</p>

                  {restaurant.rating !== null && (
                    <div className="mb-1 flex items-center gap-1">
                      <StarRating value={restaurant.rating} />
                      <span className="text-xs text-muted-foreground">{restaurant.rating}</span>
                      {restaurant.userRatingsTotal !== null && (
                        <span className="text-xs text-muted-foreground">({restaurant.userRatingsTotal})</span>
                      )}
                    </div>
                  )}

                  <div className="mb-2 flex flex-wrap gap-1">
                    {restaurant.priceLevel !== null && (
                      <Badge variant="outline" className="h-5 text-[0.7rem]">
                        {PRICE_LABELS[restaurant.priceLevel] || `Level ${restaurant.priceLevel}`}
                      </Badge>
                    )}
                    {restaurant.isOpenNow !== null && (
                      <Badge
                        variant="outline"
                        className={cn(
                          'h-5 text-[0.7rem]',
                          restaurant.isOpenNow && 'border-emerald-500 text-emerald-600 dark:text-emerald-400',
                        )}
                      >
                        {restaurant.isOpenNow ? 'Open Now' : 'Closed'}
                      </Badge>
                    )}
                  </div>

                  {restaurant.address && (
                    <p className="mb-2 flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {restaurant.address}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleSelect(restaurant)}>
                      Select
                    </Button>
                    {restaurant.googleMapsUrl && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        asChild
                      >
                        <a href={restaurant.googleMapsUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3.5 w-3.5" />
                          Maps
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
